import { ReactNode, useEffect, useMemo, useState } from "react";
import { matchPath, useLocation } from "react-router";

import { BreadcrumbContext, BreadcrumbContextData, BreadcrumbPathResolverHook, RegisteredBreadcrumbData } from "./BreadcrumbContext";

/** The home node. */
const HomeNode: RegisteredBreadcrumbData = {
  title: "Home",
  path: "/",
  parentIndex: null,
};

/** The default path resolver hook, resolving each path to itself. */
const defaultUsePathResolver: BreadcrumbPathResolverHook = () => (path) => path;

/** The `BreadcrumbRoot` component properties. */
export interface BreadcrumbRootProps {
  /** A hook returning the resolver used for each registered breadcrumb path. Defaults to the identity resolver. */
  readonly usePathResolver?: BreadcrumbPathResolverHook;

  /** Invoked with the active breadcrumb title (or null when none) whenever it changes. */
  readonly onActiveTitleChange?: (title: string | null) => void;

  /** The component children. */
  readonly children?: ReactNode;
}

/** The breadcrumb component. */
export const BreadcrumbRoot = (props: BreadcrumbRootProps): ReactNode => {
  const { children, usePathResolver = defaultUsePathResolver, onActiveTitleChange } = props;

  const [items, setItems] = useState<ReadonlyArray<RegisteredBreadcrumbData>>([HomeNode]);
  const [titleOverride, setTitleOverride] = useState<{ pathname: string; title: string } | null>(null);
  const location = useLocation();

  useEffect(() => {
    setTitleOverride((prevTitleOverride) => {
      if (!prevTitleOverride) return prevTitleOverride;
      if (prevTitleOverride.pathname === location.pathname) return prevTitleOverride;
      return null;
    });
  }, [location]);

  const nodes = useMemo((): ReadonlyArray<RegisteredBreadcrumbData> => {
    let node =
      items.find((entry) => {
        const { path } = entry;
        const { pathname } = location;

        if (!matchPath({ path }, pathname)) return false;

        return true;
      }) || null;

    let result: ReadonlyArray<RegisteredBreadcrumbData> = [];

    while (node) {
      result = [node, ...result];
      node = node.parentIndex !== null ? items[node.parentIndex] : null;
    }

    return result;
  }, [items, location]);

  const contextData = useMemo((): BreadcrumbContextData => {
    return {
      nodes,
      titleOverride,
      usePathResolver,
      setItems: (items): (() => void) => {
        setItems([HomeNode, ...items]);

        return (): void => {
          setItems((previousItems) => {
            if (previousItems.length === 1) return previousItems;
            return [HomeNode];
          });
        };
      },
      setTitle: (title): void => {
        const { pathname } = location;
        setTitleOverride(title ? { pathname, title } : null);
      },
    };
  }, [nodes, location, titleOverride, usePathResolver]);

  // Report the active breadcrumb title according to the current nodes.
  useEffect(() => {
    if (!onActiveTitleChange) return;

    const lastNode = nodes[nodes.length - 1];
    let activeTitle: string | null = null;

    if (lastNode) {
      activeTitle = titleOverride ? titleOverride.title : lastNode.title;
    }

    onActiveTitleChange(activeTitle);
  }, [nodes, titleOverride, onActiveTitleChange]);

  return (
    <>
      <BreadcrumbContext.Provider value={contextData}>{children}</BreadcrumbContext.Provider>
    </>
  );
};
